/**
 * New Year party flyers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearPartyFlyersPack: ToilePack = {
    slug: 'new-year-party-flyers',
    category: 'season',
    name: 'New Year party flyers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'club', label: 'Club night', subject: 'a flyer lettered "NEW YEAR\'S EVE" over a disco ball and rays, a blank panel for date and place'},
        {key: 'fireworks', label: 'Fireworks display', subject: 'a flyer lettered "FIREWORKS" over a burst above a hill, a blank panel for details'},
        {key: 'family', label: 'Family countdown', subject: 'a flyer lettered "FAMILY COUNTDOWN" with cartoon balloons and a clock, a blank panel for details'},
    ],
    styles: [
        {key: 'glam', label: 'Black and gold', style: 'black-and-gold new year printable, confetti borders, elegant serif lettering, print-ready', palette: 'Black, gold and champagne cream', framing: 'Laid out on a vertical page with a gold border and an even margin, the headline centred, print-ready.'},
        {key: 'confetti', label: 'Confetti party', style: 'bright confetti party printable, streamers, rounded bold lettering, print-ready', palette: 'Gold, pink, teal and white', framing: 'Laid out on a vertical page with a confetti border and a white margin, the headline centred, print-ready.'},
        {key: 'minimal', label: 'Minimal', style: 'minimal new year printable, thin lines, generous white space, print-ready', palette: 'Black and gold on white', framing: 'Laid out on a vertical page with wide margins, the headline centred, print-ready.'},
    ],
    directions: ['for a birthday party instead', 'as a save the date', 'for a rooftop party'],
    directionHint: 'try: for a birthday party instead',
    seo: {
        title: 'New Year Flyers: Club Night, Fireworks & Family',
        metaDescription: 'Nine New Year event flyers — a club night, a fireworks display, and a family countdown — with a blank details panel, in three festive styles.',
        h1: 'New Year flyers: club night, fireworks, family countdown',
        tagline: 'Nine flyers, three event types, three styles each — headline lettered, details left blank.',
        intro: 'Three New Year event flyers — a club night, a village fireworks display, and a family countdown party — each with the headline lettered and a blank panel for the organizer to fill in, in black-and-gold, confetti-party, and minimal styles. Ask for a different event type or a sponsor line, and the same flyer grammar carries it.',
        useCases: [
            'a nightclub NYE promotion',
            'a town fireworks announcement',
            'a family countdown party invite',
            'a community noticeboard flyer',
        ],
        faq: [
            {q: 'Is the date and location already filled in?', a: 'No — the details panel prints blank; write or print the date, time, and place in afterward.'},
            {q: 'What size do these print at?', a: 'Each flyer is a single vertical page, close to US letter or A4 proportions.'},
            {q: 'Can I get a different event type?', a: 'Yes — the direction field takes any variation, including a different event or venue.'},
            {q: 'What are the three styles?', a: 'Black-and-gold, bright confetti-party, and minimal thin-line.'},
        ],
    },
}
